import { Controller, Profile, ProfileGoogle } from "controller/controller-rpc";
import { computed, ComputedRef } from "vue";

// Helper to check if a profile is a Google profile
export function isGoogleProfile(
  profile: Profile
): profile is Profile & { kind: ProfileGoogle & { provider: "google" } } {
  return profile.kind.provider === "google";
}

/**
 * Approximation of the server-side admin flag, which no API surfaces to the
 * app: admin-only entry points are offered to Routine staff, and the API
 * rejects the calls behind them for anybody else.
 */
export function isAdminIdentity(email: string) {
  return email.endsWith("@routine.co");
}

// Way to detect internal testers and Google staff.
export function isBetaTesterIdentity(email: string) {
  return (
    email.endsWith("@routine.co") ||
    (email.startsWith("oauthtest") && email.endsWith("@gmail.com"))
  );
}

/**
 * Yields `true` iff the current logged-in user is a beta-tester.
 * Returns a computed ref and a release function. The caller is responsible
 * for calling `release()` when the value is no longer needed (e.g., on component unmount).
 */
export async function profileIsBetaTester(
  controller: Controller
): Promise<{ isBetaTester: ComputedRef<boolean>; release: () => void }> {
  const identities = await controller.auth.profiles();
  return {
    isBetaTester: computed(
      () =>
        identities.value.find((p) => isBetaTesterIdentity(p.email)) !==
        undefined
    ),
    release: () => identities.release(),
  };
}
